import { api, API, apiPublic, sleep } from "./_apiClient";
import { downloadBlob } from "./_apiDownloads";
import { getUserToken } from "./userApi";

// Public re-exports so external callers keep their existing `import { api,
// API, apiPublic } from "./api"` imports working unchanged.
export { api, API, apiPublic };

// Cases — start job + poll until done
export async function analyzeCase(payload, onProgress) {
  const start = await api.post("/cases/analyze", payload).then((r) => r.data);
  const jobId = start.job_id;
  while (true) {
    await sleep(1500);
    const s = await api.get(`/cases/analyze/jobs/${jobId}`).then((r) => r.data);
    if (onProgress) onProgress({ progress: s.progress || 0 });
    if (s.status === "done") return s.case;
    if (s.status === "error") throw new Error(s.error || "خطأ غير معروف");
  }
}
export const listCases = () => api.get("/cases").then((r) => r.data.cases);
export const getCase = (id) => api.get(`/cases/${id}`).then((r) => r.data);
export const deleteCase = (id) => api.delete(`/cases/${id}`).then((r) => r.data);

// Poll a draft/rewrite job until it's `done` or `error`. Monotonic guard
// on `partial` so multi-worker prod (see /app/backend/core/jobs.py) can't
// flash the UI to an empty string when a poll lands on a non-owner pod.
async function _pollDraftJob(jobId, onProgress) {
  let lastLen = 0;
  while (true) {
    await sleep(400);
    const s = await api.get(`/documents/draft/jobs/${jobId}`).then((r) => r.data);
    const partial = s.partial || "";
    if (onProgress && partial.length >= lastLen) {
      lastLen = partial.length;
      onProgress({ progress: s.progress || 0, partial });
    }
    if (s.status === "done") return s.document;
    if (s.status === "error") throw new Error(s.error || "خطأ غير معروف");
  }
}
// Documents — start job + stream live partial text via polling.
export async function draftDocument(payload, onProgress) {
  const { job_id } = await api.post("/documents/draft", payload).then((r) => r.data);
  return _pollDraftJob(job_id, onProgress);
}
export const listDocuments = () => api.get("/documents").then((r) => r.data.documents);
export const getDocument = (id) => api.get(`/documents/${id}`).then((r) => r.data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`).then((r) => r.data);
export const documentPdfUrl = (id) => `${API}/documents/${id}/pdf`;
export const documentDocxUrl = (id) => `${API}/documents/${id}/docx`;
// Static list of tone presets for the "rewrite in different tone" picker.
export const listRewriteTones = () =>
  api.get("/documents/rewrite/tones").then((r) => r.data.tones);
// Tone-shifted rewrite of an existing draft — shares the draft/jobs poll.
export async function rewriteDocument(docId, tone, onProgress) {
  const { job_id } = await api
    .post(`/documents/${docId}/rewrite`, { tone })
    .then((r) => r.data);
  return _pollDraftJob(job_id, onProgress);
}

export const downloadDocumentPdf = (id, baseName = "lexiq-document") =>
  downloadBlob(`/documents/${id}/pdf`, `${baseName}.pdf`);
export const downloadDocumentDocx = (id, baseName = "lexiq-document") =>
  downloadBlob(`/documents/${id}/docx`, `${baseName}.docx`);

// Convert a finished draft into a new stage on the parent case so the
// document becomes part of the case timeline (one-click filing).
export const attachDocumentToCase = (docId, payload) =>
  api.post(`/documents/${docId}/attach-to-case`, payload).then((r) => r.data);

// Meta
export const listLaws = () => api.get("/laws").then((r) => r.data.laws);
export const listArticles = (params = {}) =>
  api.get("/articles", { params }).then((r) => r.data.articles);
export const listCourts = () => api.get("/courts").then((r) => r.data.courts);
export const listTemplates = () => api.get("/templates").then((r) => r.data.templates);

// Bar Association card scanning — PUBLIC endpoints (no auth required,
// used during signup before the lawyer has an account).
async function postCardImage(path, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post(path, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000,
  });
  return res.data;
}
export const scanBarCardFront = (file) => postCardImage("/auth/bar-card/scan-front", file);
export const scanBarCardBack  = (file) => postCardImage("/auth/bar-card/scan-back", file);

// Voice
export async function transcribeVoice(file) {
  const fd = new FormData();
  fd.append("audio", file);
  const res = await api.post("/voice/transcribe", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return res.data;
}
export const summarizeVoice = (transcript) =>
  api.post("/voice/summarize", { transcript }).then((r) => r.data);

export async function extractStageVoice(file) {
  const fd = new FormData();
  fd.append("audio", file);
  const res = await api.post("/voice/extract-stage", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return res.data;
}

// Court decision OCR — async job pattern (avoids 60s ingress timeout).
export async function extractDecision(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const start = await api.post("/documents/extract-decision", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }).then((r) => r.data);
  const jobId = start.job_id;
  // Poll every 1s (was 1.5s) — smoother progress updates for the UI without
  // measurably increasing server load (each poll is a dict lookup in JOBS).
  while (true) {
    await sleep(1000);
    const s = await api.get(`/documents/extract-decision/jobs/${jobId}`).then((r) => r.data);
    if (onProgress) onProgress({ progress: s.progress || 0 });
    if (s.status === "done") return { extracted: s.extracted, warning: s.warning };
    if (s.status === "error") throw new Error(s.error || "تعذّر تحليل القرار");
  }
}

// Timeline / Events
export const listEvents = () => api.get("/events").then((r) => r.data.events);
export const createEvent = (data) => api.post("/events", data).then((r) => r.data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data).then((r) => r.data);
export const deleteEvent = (id) => api.delete(`/events/${id}`).then((r) => r.data);

// Clients
export const listClients = () => api.get("/clients").then((r) => r.data.clients);
export const getClient = (id) => api.get(`/clients/${id}`).then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((r) => r.data);
export const getClientPortal = (token) =>
  apiPublic.get(`/portal/${token}`).then((r) => r.data);
export const logClientReminder = (id) =>
  api.post(`/clients/${id}/reminder`).then((r) => r.data);
export const logPortalShare = (id, channel) =>
  api.post(`/clients/${id}/portal-share`, { channel }).then((r) => r.data);
export const explainToClient = (data) =>
  api.post("/clients/explain", data, { timeout: 90000 }).then((r) => r.data);

// Cassation decisions
export const listCassationPage = (params = {}) =>
  api.get("/cassation", { params }).then((r) => r.data);
export const cassationCount = (params = {}) =>
  api.get("/cassation/count", { params }).then((r) => r.data.total);
export const cassationCountFull = (params = {}) =>
  api.get("/cassation/count", { params: { ...params, include_categories: true } })
    .then((r) => r.data);
export const listCassation = (params = {}) =>
  api.get("/cassation", { params }).then((r) => r.data.decisions);
export const listCassationUploaders = () =>
  api.get("/cassation/uploaders").then((r) => r.data.uploaders);
export const checkCassationNumber = (decision_number, exclude_id = "") =>
  api.get("/cassation/check-number", {
    params: { decision_number, exclude_id: exclude_id || undefined },
  }).then((r) => r.data);
export const extractCassationArticles = (body) =>
  api.post("/cassation/extract-articles", body).then((r) => r.data);
export const getCassation = (id) => api.get(`/cassation/${id}`).then((r) => r.data);
export const createCassation = (data) => api.post("/cassation", data).then((r) => r.data);
export const deleteCassation = (id) => api.delete(`/cassation/${id}`).then((r) => r.data);
export const seedCassation = () => api.post("/cassation/seed").then((r) => r.data);

// Case stages (timeline + attachments)
export const listStages = (caseId) =>
  api.get(`/cases/${caseId}/stages`).then((r) => r.data);
export const createStage = (caseId, data) =>
  api.post(`/cases/${caseId}/stages`, data).then((r) => r.data);
export const updateStage = (caseId, stageId, data) =>
  api.put(`/cases/${caseId}/stages/${stageId}`, data).then((r) => r.data);
export const deleteStage = (caseId, stageId) =>
  api.delete(`/cases/${caseId}/stages/${stageId}`).then((r) => r.data);
export const recordStageShare = (caseId, stageId) =>
  api.post(`/cases/${caseId}/stages/${stageId}/share`).then((r) => r.data);
export const fetchMyStats = () => api.get("/stats/me").then((r) => r.data);
export const triggerWeeklyChallengeReminder = (force = true) =>
  api.post(`/stats/weekly-challenge/run?force=${force}`).then((r) => r.data);
export const listStageTypes = () => api.get("/case-stages/types").then((r) => r.data);
export const uploadStageAttachment = (caseId, stageId, file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/cases/${caseId}/stages/${stageId}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const deleteStageAttachment = (caseId, stageId, fileId) =>
  api.delete(`/cases/${caseId}/stages/${stageId}/attachments/${fileId}`)
    .then((r) => r.data);
export const stageAttachmentUrl = (caseId, stageId, fileId) =>
  `${api.defaults.baseURL}/cases/${caseId}/stages/${stageId}/attachments/${fileId}`;

// Chat
export const createSession = (title) =>
  api.post("/chat/sessions", { title }).then((r) => r.data);
export const listSessions = () => api.get("/chat/sessions").then((r) => r.data.sessions);
export const deleteSession = (id) => api.delete(`/chat/sessions/${id}`).then((r) => r.data);
export const listMessages = (sessionId) =>
  api.get(`/chat/sessions/${sessionId}/messages`).then((r) => r.data.messages);
export const listArchivedSessions = () =>
  api.get("/chat/archive").then((r) => r.data.sessions);
export const loadArchivedSession = (sessionId) =>
  api.get(`/chat/archive/${sessionId}`).then((r) => r.data);

// Streaming chat — returns an async iterator of text deltas
async function* readSSE(res) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const json = line.slice(5).trim();
      if (!json) continue;
      try { yield JSON.parse(json); }
      catch { /* ignore parse errors — server may emit heartbeat lines */ }
    }
  }
}

export async function* streamChatMessage(sessionId, message) {
  const t = getUserToken();
  const headers = { "Content-Type": "application/json" };
  if (t) headers.Authorization = `Bearer ${t}`;
  const res = await fetch(`${API}/chat/sessions/${sessionId}/message`, {
    method: "POST", headers, body: JSON.stringify({ message }),
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  yield* readSSE(res);
}

// Leaderboard + Public Lawyers Directory
export const getLeaderboard = () => api.get("/leaderboard").then((r) => r.data);
export const getMyViewStats = () => api.get("/lawyers/me/views").then((r) => r.data);
export const attachBarCard = (payload) =>
  api.put("/auth/me/bar-card", payload).then((r) => r.data);
export const updatePublicPresence = (payload) =>
  api.put("/auth/profile/public-presence", payload).then((r) => r.data);
export async function uploadAvatar(blob) {
  const fd = new FormData();
  fd.append("file", blob, "avatar.png");
  const res = await api.post("/auth/profile/avatar", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}
export const publicLawyers = (params = {}) =>
  apiPublic.get("/lawyers/public", { params }).then((r) => r.data);
export const publicLawyerProfile = (slug) =>
  apiPublic.get(`/lawyers/${slug}`).then((r) => r.data);
export const avatarUrl = (id) => (id ? `${API}/lawyers/avatar/${id}` : "");

// Admin
export const adminGetLeaderboardConfig = () =>
  api.get("/admin/leaderboard-config").then((r) => r.data);
export const adminUpdateLeaderboardConfig = (payload) =>
  api.put("/admin/leaderboard-config", payload).then((r) => r.data);

// Active sessions (concurrent-device control)
export const listMySessions = () => api.get("/auth/sessions").then((r) => r.data);
export const revokeMySession = (sessionId) =>
  api.delete(`/auth/sessions/${sessionId}`).then((r) => r.data);
export const signOutOtherSessions = () =>
  api.post("/auth/sessions/sign-out-others").then((r) => r.data);

// Internal Regulations (الأنظمة الداخلية) — public read-only surface.
export const listRegulations = ({ q, category } = {}) =>
  api.get("/regulations", { params: { q, category } }).then((r) => r.data.regulations || []);
export const getRegulation = (regulationId) =>
  api.get(`/regulations/${regulationId}`).then((r) => r.data);
export const listRegulationArticles = (regulationId, { q } = {}) =>
  api.get(`/regulations/${regulationId}/articles`, { params: { q } })
    .then((r) => r.data.articles || []);
